import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    // Guard against React StrictMode double-mount AND back-button replays:
    // each session_id from Emergent auth can only be exchanged ONCE upstream.
    const consumedKey = `rmj_consumed_session_id_${sessionId}`;
    if (sessionStorage.getItem(consumedKey)) {
      // Already exchanged — rely on the cookie + /auth/me check.
      window.history.replaceState({}, "", "/");
      navigate("/", { replace: true });
      return;
    }
    sessionStorage.setItem(consumedKey, "1");

    // Strip the hash IMMEDIATELY so a stray remount can't re-read it.
    window.history.replaceState({}, "", "/");

    (async () => {
      try {
        const { data } = await api.post("/auth/session-data", { session_id: sessionId });
        if (data.session_token) setAuthToken(data.session_token);
        setUser(data.user);
        toast.success(`Welcome, ${data.user.name || data.user.email}`);
        navigate("/", { replace: true, state: { user: data.user } });
      } catch (e) {
        // Surface as much diagnostic info as possible — visible without devtools.
        const status = e?.response?.status ?? "no-response";
        const detail = e?.response?.data?.detail;
        const raw = typeof detail === "string" ? detail : (e?.message || "Sign-in failed.");
        const msg = `Sign-in failed [${status}]: ${raw}`;
        // Persist the detail so the Login page can render it in a banner too.
        try { sessionStorage.setItem("rmj_last_signin_error", msg); } catch (err) { /* ignore */ }
        toast.error(msg, { duration: 12000 });
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="App grain min-h-screen flex items-center justify-center" data-testid="auth-callback-page">
      <div className="text-[#1d3a14] brand-serif text-2xl">Signing you in…</div>
    </div>
  );
}
